import { useRef, useEffect, useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Button,
  Text,
  Spinner,
  Tooltip,
  Link,
} from '@fluentui/react-components';
import { ottoTokens } from '@/theme/ottoTokens';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  ChatRegular,
  PersonRegular,
  BotRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  CircleFilled,
} from '@fluentui/react-icons';
import type { ChatMessageEntry } from '@/services/monitoringApi';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { formatDateTime as canonicalFormatDateTime } from '@/utils/dateTime';

type ChatLogPanelProps = {
  /** Chat history messages from the run */
  chatHistory?: ChatMessageEntry[] | undefined;
  /** Whether the chat history is loading */
  isLoading?: boolean | undefined;
  /** Whether the panel is collapsed */
  isCollapsed?: boolean;
  /** Called when collapse state should change */
  onCollapseChange?: (collapsed: boolean) => void;
  /** Called when a tool entry is clicked - receives nodeId, iteration, and optional toolId */
  onToolClick?: (nodeId: string, iteration: number, toolId?: string) => void;
  /** Called when iteration changes - used to sync canvas display */
  onIterationChange?: (agentName: string, iteration: number) => void;
  /** Set of tool names that exist in the execution log (used to determine clickability) */
  executionLogToolNames?: Set<string>;
};

const COLLAPSED_WIDTH = 32;

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    flex: 1,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  // Collapsed state - thin vertical bar
  collapsedContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: `${COLLAPSED_WIDTH}px`,
    minWidth: `${COLLAPSED_WIDTH}px`,
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: tokens.spacingVerticalS,
    gap: tokens.spacingVerticalS,
  },
  collapsedIcon: {
    color: tokens.colorNeutralForeground2,
    fontSize: '16px',
  },
  collapsedTitle: {
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    minWidth: 'auto',
    padding: '4px',
  },
  collapseButton: {
    minWidth: 'auto',
    padding: '4px',
  },
  header: {
    // Match LogsPanel's columnHeader exactly (48px fixed height, same
    // border + horizontal padding) so the Chat log header and the
    // Execution log column headers sit on the same baseline and share
    // an unbroken bottom border across the whole bottom panel.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingInline: tokens.spacingHorizontalM,
    height: '48px',
    boxSizing: 'border-box',
    flexShrink: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  headerSubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    ...customScrollbarStyles,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    padding: tokens.spacingHorizontalL,
  },
  emptyStateTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground2,
  },
  emptyStateText: {
    fontSize: tokens.fontSizeBase200,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
    height: '100%',
  },
  // Message styles
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  messageUser: {
    alignItems: 'flex-end',
  },
  messageAssistant: {
    alignItems: 'flex-start',
  },
  messageBubble: {
    maxWidth: '85%',
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  messageBubbleUser: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderBottomRightRadius: tokens.borderRadiusSmall,
  },
  messageBubbleAssistant: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderBottomLeftRadius: tokens.borderRadiusSmall,
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  messageRole: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground3,
  },
  messageTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
  },
  messageIcon: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  // Tool entry styles
  toolEntry: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalS,
  },
  toolEntryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  toolStatusIcon: {
    fontSize: '14px',
    flexShrink: 0,
  },
  toolStatusSucceeded: {
    color: ottoTokens.statusSucceeded,
  },
  toolStatusFailed: {
    color: ottoTokens.statusFailed,
  },
  toolStatusPending: {
    color: ottoTokens.statusPending,
  },
  toolName: {
    fontSize: tokens.fontSizeBase200,
  },
  toolNameClickable: {
    cursor: 'pointer',
  },
  toolNameStatic: {
    color: tokens.colorNeutralForeground2,
  },
  toolTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    marginLeft: tokens.spacingHorizontalXS,
  },
});

/**
 * Format tool name for display - converts snake_case/PascalCase to readable format
 */
const formatToolName = (toolName: string): string => {
  // Replace underscores with spaces and handle camelCase/PascalCase
  return toolName
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
};

/**
 * Get status icon component based on tool status
 */
const ToolStatusIcon = ({ status, className }: { status: string; className: string }) => {
  const styles = useStyles();
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'succeeded') {
    return <CheckmarkCircleFilled className={mergeClasses(className, styles.toolStatusSucceeded)} />;
  }
  if (normalizedStatus === 'failed') {
    return <DismissCircleFilled className={mergeClasses(className, styles.toolStatusFailed)} />;
  }
  // For pending, running, or unknown statuses
  return <CircleFilled className={mergeClasses(className, styles.toolStatusPending)} />;
};

/**
 * ChatLogPanel displays a read-only view of chat history from a workflow run.
 * Used in monitoring mode to display the conversation that occurred during execution.
 * Includes both content messages (User/Assistant) and tool execution results.
 */
export const ChatLogPanel = ({
  chatHistory,
  isLoading = false,
  isCollapsed = false,
  onCollapseChange,
  onToolClick,
  onIterationChange,
  executionLogToolNames,
}: ChatLogPanelProps) => {
  const styles = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Process all messages (Content + ToolResult) sorted by timestamp
  const allMessages = useMemo(() => {
    if (!chatHistory) return [];

    return chatHistory
      .filter(msg => {
        // Include Content messages with actual content
        if (msg.messageEntryType === 'Content') {
          return (msg.role === 'User' || msg.role === 'Assistant') &&
                 msg.messageEntryPayload?.content;
        }
        // Include ToolResult messages with tool info
        if (msg.messageEntryType === 'ToolResult') {
          return msg.toolResultsPayload?.toolResult?.toolName;
        }
        return false;
      })
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [chatHistory]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages]);

  const formatDateTime = (timestamp: string) => canonicalFormatDateTime(timestamp, 'short');

  // Chat-specific time formatter (hour12 + seconds) kept inline — the chat
  // panel is the only surface that uses this exact shape.
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const handleToolClick = (message: ChatMessageEntry) => {
    const toolResult = message.toolResultsPayload?.toolResult;
    if (!toolResult) return;

    const agentName = message.agentMetadata?.agentName ?? '';
    const toolName = toolResult.toolName;
    const iteration = message.iteration ?? 0;

    // Construct nodeId in the format expected by the execution panel
    const nodeId = agentName ? `tool-${agentName}-${toolName}` : toolName;

    onToolClick?.(nodeId, iteration, toolName);
    if (agentName) {
      onIterationChange?.(agentName, iteration);
    }
  };

  // Collapsed state - show thin vertical bar
  if (isCollapsed) {
    return (
      <div className={styles.collapsedContainer}>
        <Tooltip content="Expand Chat log" relationship="label" positioning="after">
          <Button
            className={styles.expandButton}
            appearance="subtle"
            size="small"
            icon={<ChevronRightRegular />}
            onClick={() => onCollapseChange?.(false)}
            aria-label="Expand Chat log"
          />
        </Tooltip>
        <ChatRegular className={styles.collapsedIcon} />
        <Text className={styles.collapsedTitle}>Chat log</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Text className={styles.headerTitle}>Chat log</Text>
          <Text className={styles.headerSubtitle}>Read-only</Text>
        </div>
        {onCollapseChange && (
          <Tooltip content="Collapse" relationship="label">
            <Button
              className={styles.collapseButton}
              appearance="subtle"
              size="small"
              icon={<ChevronLeftRegular />}
              onClick={() => onCollapseChange(true)}
              aria-label="Collapse Chat log"
            />
          </Tooltip>
        )}
      </div>

      <div
        className={styles.messagesContainer}
        role="log"
        aria-label="Chat log messages"
      >
        {isLoading && allMessages.length === 0 ? (
          <div className={styles.loadingContainer}>
            <Spinner size="small" label="Loading chat history..." />
          </div>
        ) : allMessages.length === 0 ? (
          <div className={styles.emptyState}>
            <Text className={styles.emptyStateTitle}>No chat history</Text>
            <Text className={styles.emptyStateText}>
              This workflow has not processed any chat messages yet.
            </Text>
          </div>
        ) : (
          <>
            {allMessages.map((message, index) => {
              // Render tool result entry
              if (message.messageEntryType === 'ToolResult') {
                const toolResult = message.toolResultsPayload?.toolResult;
                if (!toolResult) return null;

                // Tool is only clickable if:
                // 1. We have a click handler and agent metadata
                // 2. The tool exists in the execution log (not a built-in tool like code_interpreter)
                const toolExistsInExecutionLog = executionLogToolNames?.has(toolResult.toolName) ?? false;
                const hasClickHandler = onToolClick &&
                                        message.agentMetadata?.agentName &&
                                        toolExistsInExecutionLog;

                return (
                  <div
                    key={`tool-${message.timestamp}-${index}`}
                    className={styles.toolEntry}
                    role="article"
                    aria-label={`Tool: ${toolResult.toolName}`}
                  >
                    <div className={styles.toolEntryRow}>
                      <ToolStatusIcon
                        status={toolResult.status}
                        className={styles.toolStatusIcon}
                      />
                      {hasClickHandler ? (
                        <Link
                          className={mergeClasses(styles.toolName, styles.toolNameClickable)}
                          onClick={() => handleToolClick(message)}
                        >
                          {formatToolName(toolResult.toolName)}
                        </Link>
                      ) : (
                        <Text className={mergeClasses(styles.toolName, styles.toolNameStatic)}>
                          {formatToolName(toolResult.toolName)}
                        </Text>
                      )}
                    </div>
                    <Text className={styles.toolTime}>
                      {formatTime(message.timestamp)}
                    </Text>
                  </div>
                );
              }

              // Render content message (User/Assistant)
              const isUser = message.role === 'User';
              const content = message.messageEntryPayload?.content ?? '';

              return (
                <div
                  key={`msg-${message.timestamp}-${index}`}
                  className={mergeClasses(
                    styles.messageContainer,
                    isUser ? styles.messageUser : styles.messageAssistant
                  )}
                  role="article"
                  aria-label={`${message.role} message`}
                >
                  <div className={styles.messageHeader}>
                    {isUser ? (
                      <PersonRegular className={styles.messageIcon} />
                    ) : (
                      <BotRegular className={styles.messageIcon} />
                    )}
                    <span className={styles.messageRole}>
                      {isUser ? 'User' : (message.agentMetadata?.agentName ?? 'Agent')}
                    </span>
                    <span className={styles.messageTime}>
                      {formatDateTime(message.timestamp)}
                    </span>
                  </div>
                  <div
                    className={mergeClasses(
                      styles.messageBubble,
                      isUser ? styles.messageBubbleUser : styles.messageBubbleAssistant
                    )}
                  >
                    {content}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </div>
  );
};
